import { FormikProps } from 'formik'
import { toUpper } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AddressBookButton } from '@src/components/CheckoutModal/Checkout/DeliveryFulfilmentStep/AddressBookButton'
import { OrderSummary } from '@src/components/CheckoutModal/Checkout/OrderSummary/OrderSummary'
import { CheckoutButtonWrapperMobile } from '@src/components/CheckoutModal/CheckoutButtonWrapperMobile'
import {
  FlexGrowForm,
  FlexGrowScrollContainer,
  FulfilmentNoteContainer,
  FulfilmentSectionContainer,
  StepTitle,
  TitleAndErrorContainer,
  ErrorMessage,
} from '@src/components/CheckoutModal/FormElements.styles'
import { UncontrolledTextInput } from '@src/components/Inputs/TextInput/TextInput'
import { Totals } from '@src/components/Totals/Totals'
import { LocationType } from '@src/graphql-types'
import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import { useCustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import { useBreakpoint } from '@src/hooks/useBreakpoint'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { useSafeArea } from '@src/hooks/useSafeArea'

import { AddAddressSection } from './AddAddressSection'
import { InnerScrollContainer } from './DeliveryFulfilmentStep.styles'
import { ExistingAddressSection } from './ExistingAddressSection'
import {
  AddressType,
  DeliveryFulfilmentFormSchema,
  EMPTY_ADDRESS,
} from './useDeliveryFulfilmentFormikProps/schema'
import { CheckoutButton } from '../../CheckoutButton'

export const DeliveryFulfilmentForm: React.FC<
  FormikProps<DeliveryFulfilmentFormSchema>
> = ({
  values,
  isSubmitting,
  handleChange,
  setFieldValue,
  initialValues,
  handleSubmit,
  errors,
}) => {
  const { isMobile } = useBreakpoint()
  const { safeAreaInsetBottom } = useSafeArea()
  const marketplace = useMarketplace()
  const { t } = useTranslation('checkout')

  const containerRef = useRef<HTMLDivElement>(null)
  const [addressError, setAddressError] = useState(false)

  useEffect(() => {
    if (errors.addressId) {
      setAddressError(true)
    }
  }, [errors])

  useEffect(() => {
    if (containerRef && containerRef.current && addressError) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [addressError])

  const { outlet } = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })
  const fulfilmentFilter = useFulfilmentFilter()

  const { data, loading, error } = useCustomerDetailsAndAddressesQuery({
    addressAcceptingOrdersToOutletId: outlet.id,
  })

  const addresses = data?.customerDetails.deliveryAddresses || []
  const defaultAddress = addresses.find(address => address.default)

  useEffect(() => {
    const location = fulfilmentFilter.data.where.location

    if (location.type === LocationType.PLACE_SEARCH && location.address) {
      const addressParts = location.address
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)

      // Made some assumptions here that the last part of the address is the country,
      // the second last part is the postcode
      // and the third last part is the city -
      // this is just to try and pre-populate the add address form as much as possible.
      // The user can edit this before submission

      const [firstLine, ...remainingParts] = addressParts
      const postcode = remainingParts[remainingParts.length - 2]
      const city = remainingParts[remainingParts.length - 3]
      const secondLine = remainingParts.slice(0, -3).join(', ')

      setFieldValue('addressType', AddressType.NEW)
      setFieldValue('addressId', '')
      setFieldValue('addAddress', {
        ...EMPTY_ADDRESS,
        firstLine: firstLine || '',
        secondLine: secondLine || '',
        city: city || '',
        postcode: postcode?.trim() || '',
        countryCodeStr: marketplace.country.ISO3166Alpha2,
      })
      return
    }

    if (
      (location.type === LocationType.POSTCODE ||
        location.type === LocationType.COORDINATES) &&
      addresses.length &&
      defaultAddress
    ) {
      setFieldValue('addressId', defaultAddress?.id)
      setFieldValue('addressType', AddressType.EXISTING)
    }
  }, [
    addresses,
    defaultAddress,
    fulfilmentFilter.data.where.location,
    marketplace.country.ISO3166Alpha2,
    setFieldValue,
  ])

  return (
    <FlexGrowForm
      $windowHeight={window.innerHeight}
      $hasSafeArea={safeAreaInsetBottom > 0}
      data-test-id="checkout-form-delivery"
    >
      <FlexGrowScrollContainer
        id="fulfilment-container"
        data-test-id="checkout-container-fulfilment"
      >
        <InnerScrollContainer>
          <TitleAndErrorContainer
            addressError={addressError}
            ref={containerRef}
            data-test-id="checkout-container-titleanderror"
          >
            <StepTitle data-test-id="checkout-title-selectaddress">
              {t('select_or_add_address')}{' '}
              <AddressBookButton data-test-id="checkout-button-addressbook">
                {t('address_book')}
              </AddressBookButton>
            </StepTitle>
            {addressError && <ErrorMessage>{t('address_error')}</ErrorMessage>}
          </TitleAndErrorContainer>

          <FulfilmentSectionContainer
            data-test-id="checkout-container-fulfilmentsection"
            onChange={e => {
              // whenever the new address radio button is selected, clear the addressId
              // and whenever an existing address is selected, update the addressType to EXISTING
              if (e.target instanceof HTMLInputElement) {
                if (
                  e.target.name === 'addressType' &&
                  e.target.value === AddressType.NEW
                ) {
                  setFieldValue('addressId', '')
                  setFieldValue(
                    'addAddress',
                    initialValues.addressType === AddressType.NEW
                      ? initialValues.addAddress
                      : EMPTY_ADDRESS
                  )
                } else if (e.target.name === 'addressId') {
                  setFieldValue('addressType', AddressType.EXISTING)
                }
              }
              setAddressError(false)
            }}
          >
            <ExistingAddressSection
              data={data}
              loading={loading}
              error={error}
            />
            <AddAddressSection t={t} values={values} />
          </FulfilmentSectionContainer>

          {/* Delivery note */}
          <FulfilmentNoteContainer>
            <UncontrolledTextInput
              name="deliveryNotes"
              touched
              label={t('delivery_note')}
              value={values.deliveryNotes}
              placeholder={
                marketplace.deliveryNoteText ?? t('delivery_note_example')
              }
              required={false}
              onChange={handleChange}
              dataTestId="checkout-input-deliverynote"
            />
          </FulfilmentNoteContainer>
          <OrderSummary
            addressId={
              values.addressType === AddressType.EXISTING
                ? values.addressId
                : ''
            }
            newAddress={
              values.addressType === AddressType.NEW
                ? `${values.addAddress.firstLine} ${values.addAddress.secondLine}`
                : ''
            }
          />
          {isMobile && (
            <Totals
              addressIdOverride={
                values.addressType === AddressType.EXISTING
                  ? values.addressId
                  : undefined
              }
            />
          )}
        </InnerScrollContainer>
      </FlexGrowScrollContainer>
      <CheckoutButtonWrapperMobile>
        {!isMobile && (
          <Totals
            addressIdOverride={
              values.addressType === AddressType.EXISTING
                ? values.addressId
                : undefined
            }
          />
        )}

        <CheckoutButton
          loading={isSubmitting}
          label={toUpper(t('continue_to_payment'))}
          onClick={e => {
            e.preventDefault()
            handleSubmit()
          }}
          dataTestId="basket-delivery-button-continuetopayment"
        />
      </CheckoutButtonWrapperMobile>
    </FlexGrowForm>
  )
}
